<div class="container full-container p-0">
  <ng-container>
    <div class="dashboard-stats-grid mb-4">
      <div class="w-full" *ngFor="let card of cardData">
        <div class="card">
          <div class="card-body">
            <div class="flex items-center gap-4">
              <div class="{{card.bgColor}} h-12 w-12 rounded-full flex items-center justify-center">
                <i class="{{card.icon}} {{card.textColor}} text-2xl"></i>
              </div>
              <div>
                <h6 class="text-xl font-bold">{{ getValues(card.key) | number }}</h6>
                <p class="text-fs_15 font-medium">{{card.label}}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-container>

  <div class="mb-4" *ngIf="hasError && errorMsg">
    <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
      {{ errorMsg }}
    </div>
  </div>

  <ng-container>
    <app-preloader *ngIf="dashboardLoading"></app-preloader>

    <div class="dashboard-analytics-grid" *ngIf="!dashboardLoading">
      <div class="dashboard-primary-panel">
        <div class="card">
          <div class="card-body">
            <div class="analytics-panel-header">
              <div>
                <h4 class="text-gray-500 text-lg font-semibold sm:mb-0 mb-1">Signups and Theme Creation</h4>
                <p class="text-sm text-gray-400" *ngIf="dashboardRangeLabel">{{ dashboardRangeLabel }}</p>
              </div>

              <div class="range-toggle" aria-label="Dashboard date range">
                <button
                  type="button"
                  class="range-toggle-button"
                  *ngFor="let days of rangeOptions"
                  [class.is-active]="dashboardRangeDays === days"
                  [attr.aria-pressed]="dashboardRangeDays === days"
                  (click)="getDashboardData(days)">
                  {{ days }} days
                </button>
              </div>
            </div>

            <div class="trend-chart-shell">
              <apx-chart
                [series]="chartOptions.series"
                [chart]="chartOptions.chart"
                [dataLabels]="chartOptions.dataLabels"
                [colors]="chartOptions.colors"
                [stroke]="chartOptions.stroke"
                [xaxis]="chartOptions.xaxis"
                [yaxis]="chartOptions.yaxis"
                [tooltip]="chartOptions.tooltip"
                [legend]="chartOptions.legend"
                [grid]="chartOptions.grid"
                [markers]="chartOptions.markers"
                [title]="chartOptions.title">
              </apx-chart>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <div class="subscription-card-header">
            <h4 class="text-gray-500 text-lg font-semibold">Subscription Mix</h4>
            <span>{{ subscriptionTotals.total | number }} total</span>
          </div>

          <div class="subscription-mix-layout">
            <div class="subscription-chart-shell">
              <apx-chart
                [series]="chartOptionsDonut.series"
                [chart]="chartOptionsDonut.chart"
                [labels]="chartOptionsDonut.labels"
                [colors]="chartOptionsDonut.colors"
                [legend]="chartOptionsDonut.legend"
                [tooltip]="chartOptionsDonut.tooltip"
                [plotOptions]="chartOptionsDonut.plotOptions"
                [dataLabels]="chartOptionsDonut.dataLabels"
                [responsive]="chartOptionsDonut.responsive">
              </apx-chart>
            </div>

            <div class="subscription-labels-inline">
              <div class="subscription-label-item" *ngFor="let item of subscriptionMixItems">
                <span class="subscription-dot" [style.background]="item.color" aria-hidden="true"></span>
                <span class="subscription-label">{{ item.shortLabel }}</span>
                <strong>{{ item.value | number }}</strong>
                <span class="subscription-percent">{{ item.percent }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="dashboard-primary-panel">
        <div class="card">
          <div class="card-body">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
              <h4 class="text-gray-500 text-lg font-semibold">Revenue by Theme</h4>
              <div class="flex flex-wrap gap-2" *ngIf="revenueTotalsByCurrency.length">
                <span class="inline-flex items-center p-3D font-semibold bg-gray-100 text-gray-500"
                  *ngFor="let total of revenueTotalsByCurrency">
                  {{ formatMoney(total.amount, total.currency) }}
                </span>
              </div>
            </div>

            <div class="relative overflow-x-auto" *ngIf="revenueByTheme.length; else noRevenue">
              <table class="text-left w-full whitespace-nowrap text-sm text-gray-500">
                <thead>
                  <tr class="text-sm">
                    <th scope="col" class="p-4 font-semibold">Theme</th>
                    <th scope="col" class="p-4 font-semibold">Currency</th>
                    <th scope="col" class="p-4 font-semibold">Revenue</th>
                    <th scope="col" class="p-4 font-semibold">Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of revenueByTheme; trackBy: trackByThemeCurrency">
                    <td class="p-4 font-medium">{{ row.themeName || '(unknown theme)' }}</td>
                    <td class="p-4">{{ row.currency }}</td>
                    <td class="p-4">{{ formatMoney(row.totalAmount, row.currency) }}</td>
                    <td class="p-4">{{ row.transactionCount | number }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <ng-template #noRevenue>
              <p class="text-sm text-gray-400">No successful transactions found.</p>
            </ng-template>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <h4 class="text-gray-500 text-lg font-semibold mb-4">Active Tags by Theme</h4>
          <apx-chart
            [series]="chartOptionsLines.series"
            [chart]="chartOptionsLines.chart"
            [dataLabels]="chartOptionsLines.dataLabels"
            [xaxis]="chartOptionsLines.xaxis"
            [yaxis]="chartOptionsLines.yaxis"
            [fill]="chartOptionsLines.fill"
            [markers]="chartOptionsLines.markers"
            [tooltip]="chartOptionsLines.tooltip">
          </apx-chart>

          <div class="mt-4" *ngIf="activeTagsByTheme.length; else noActiveTags">
            <div class="flex items-center justify-between py-2 border-b border-gray-100"
              *ngFor="let theme of activeTagsByTheme.slice(0, 5); trackBy: trackByTheme">
              <span class="text-sm text-gray-500 truncate pr-3">{{ theme.themeName || '(unknown theme)' }}</span>
              <span class="font-semibold text-gray-500">{{ theme.activeTagCount | number }}</span>
            </div>
          </div>

          <ng-template #noActiveTags>
            <p class="text-sm text-gray-400">No active tags found.</p>
          </ng-template>
        </div>
      </div>
    </div>
  </ng-container>

  <ng-container>
    <div class="card" style="margin-top: 30px;">
      <div class="card-body">
        <h6 class="text-lg text-gray-500 font-semibold mb-6">Most Recent ITagTip Users</h6>
        <app-preloader *ngIf="usersLoading"></app-preloader>
        <div class="relative overflow-x-auto" *ngIf="!usersLoading">
          <table class="text-left w-full whitespace-nowrap text-sm text-gray-500">
            <thead>
              <tr class="text-sm">
                <th scope="col" class="p-4 font-semibold">User name</th>
                <th scope="col" class="p-4 font-semibold">Device Count</th>
                <th scope="col" class="p-4 font-semibold">Last Updated</th>
                <th scope="col" class="p-4 font-semibold">Availability</th>
                <th scope="col" class="p-4 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let user of users | paginate
                : {
                    itemsPerPage: pageSize,
                    currentPage: currentPage,
                    totalItems: totalItems
                  };
                let i = index">
                <td class="p-4 text-sm">
                  <div class="flex gap-6 items-center">
                    <div class="h-12 w-12 inline-block">
                      <img [src]="user.profileImageUrl || 'assets/images/profile/no-record-found.png'" alt="" class="rounded-full w-100">
                    </div>
                    <div class="flex flex-col gap-1 text-gray-500">
                      <h3 class="font-bold">{{ user.name || 'N/A' }}</h3>
                    </div>
                  </div>
                </td>
                <td class="p-4">
                  <h3 class="font-medium">{{ user.devices?.length ? user.devices.length + ' Device(s)' : 'N/A' }}</h3>
                </td>
                <td class="p-4">
                  {{ user.devices?.length ? (user.devices[0].updatedAt | date:'medium') : 'N/A' }}
                </td>
                <td class="p-4">
                  <span class="inline-flex items-center py-2 px-4 rounded-3xl font-semibold bg-gray-200 text-gray-400">Offline</span>
                </td>
                <td class="p-4">
                  <button type="button" class="py-2 px-4 inline-flex items-center gap-x-2 font-semibold rounded-2xl border text-gray border-gray-200 hover:border-gray-200 hover:text-white hover:bg-gray-500" routerLink="/portal/user/{{user._id}}/profile">
                    <i class="ti ti-eye"></i> View
                  </button>
                </td>
              </tr>

              <tr *ngIf="!users.length">
                <td colspan="5" class="p-4 text-center text-gray-400">No recent users found.</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="5">
                  <div class="flex justify-center gap-3 mt-4 mb-4">
                    <button
                      class="px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                      [routerLink]="['/portal/users']">
                      View all itagtip users
                    </button>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </ng-container>
</div>
